/**
 * 헤더 탭(keep-alive)으로 상태가 유지되는 화면 공통 처리.
 *
 * keep-alive된 컴포넌트는 beforeDestroy가 불리지 않는다. 그래서 확장 메시지
 * 리스너를 mounted에서 붙이고 beforeDestroy에서 떼는 기존 방식을 그대로 두면,
 * 백그라운드에 남은 화면이 다른 화면의 수집 메시지까지 받아버린다.
 * 이 mixin은 "보이는 동안에만 듣는다"를 강제한다.
 *
 * 사용하는 화면이 지켜야 할 것:
 *  1) mounted에서 this.messageEventHandler 를 정의한 뒤 this.bindExtensionMessages() 호출
 *  2) 기존 beforeDestroy의 removeEventListener는 제거 (여기서 처리한다)
 *  3) Layout-1.vue의 cachedViews 에 컴포넌트 name 추가 (name은 고유해야 함)
 */
export default {
    data() {
        return {
            lastLoadedAt: null,
            extMsgBound: false,
            viewActive: true,
            ownCollectRunning: false,
            ownCollectTimer: null,
            STALE_AFTER_MS: 10 * 60 * 1000,          // 10분
            OWN_COLLECT_TIMEOUT_MS: 15 * 60 * 1000,  // 15분
        };
    },
    mounted() {
        // 최초 로딩 시점을 기록해둔다 (탭 복귀 시 재조회 판단 기준)
        this.lastLoadedAt = Date.now();
    },
    activated() {
        this.viewActive = true;
        this.bindExtensionMessages();
        // 오래 비워뒀으면 최신으로 맞추고, 그 안이면 캐시 그대로 즉시 표시
        if (this.lastLoadedAt && Date.now() - this.lastLoadedAt > this.STALE_AFTER_MS) {
            this.refreshIfStale();
        }
    },
    deactivated() {
        this.viewActive = false;
        // 내가 시작한 수집이 돌고 있으면 백그라운드에서도 결과를 받아야 한다.
        // (진행 메시지 중 itemKey 없는 것들은 ownCollectRunning 으로 걸러지므로 남의 화면은 영향 없다)
        if (!this.ownCollectRunning) this.unbindExtensionMessages();
    },
    beforeDestroy() {
        this.unbindExtensionMessages();
        this.clearOwnCollectTimer();
    },
    methods: {
        bindExtensionMessages() {
            if (this.extMsgBound || !this.messageEventHandler) return;
            window.addEventListener('message', this.messageEventHandler);
            this.extMsgBound = true;
        },
        unbindExtensionMessages() {
            if (!this.extMsgBound) return;
            window.removeEventListener('message', this.messageEventHandler);
            this.extMsgBound = false;
        },
        /**
         * 이 화면이 수집을 시작할 때 호출한다.
         * 확장이 쏘는 진행 메시지(autoCollectStatus, growthdbratio)에는 itemKey 가 없어서
         * 듣고 있는 화면이 전부 반응한다. 그래서 "내가 건 수집인지"를 화면이 스스로 알아야
         * 남의 수집 진행률을 자기 스피너에 띄우고 갇히는 일이 없다.
         */
        beginOwnCollection() {
            this.ownCollectRunning = true;
            this.bindExtensionMessages(); // 백그라운드에서 걸었더라도 결과는 받는다
            this.touchOwnCollect();
        },
        /**
         * 수집 진행 신호를 받을 때마다 호출한다. 타임아웃 타이머를 처음부터 다시 건다.
         *
         * 타임아웃은 "확장이 죽어 스피너에 영영 갇히는 것"을 막으려는 장치지 수집 시간의 상한이 아니다.
         * 예전에는 시작 시점에 한 번만 걸어서, 15분보다 오래 걸리는 수집은 멀쩡히 돌고 있는데도
         * 중간에 ownCollectRunning 이 꺼졌다. 그러면 이 플래그를 조건으로 쓰는 화면 스피너는
         * 그 시점 값에서 멈추고, 조건 없이 갱신하는 헤더 토스트만 계속 올라가 숫자가 어긋나 보인다.
         * 실측(2026-08-26, 상품 3,035개 계정): 수집 36분 → 스피너 49% 고정 / 헤더 87%.
         */
        touchOwnCollect() {
            if (!this.ownCollectRunning) return;
            this.clearOwnCollectTimer();
            this.ownCollectTimer = setTimeout(() => {
                console.warn('[keepAliveView] 수집 진행 신호가 끊겨 진행 표시를 해제합니다');
                this.endOwnCollection();
            }, this.OWN_COLLECT_TIMEOUT_MS);
        },
        endOwnCollection() {
            this.ownCollectRunning = false;
            this.clearOwnCollectTimer();
            // 백그라운드에서 끝났으면 이제 들을 이유가 없다
            if (!this.viewActive) this.unbindExtensionMessages();
        },
        clearOwnCollectTimer() {
            if (this.ownCollectTimer) {
                clearTimeout(this.ownCollectTimer);
                this.ownCollectTimer = null;
            }
        },
        async refreshIfStale() {
            if (typeof this.search !== 'function') return;
            try {
                await this.search();
                this.lastLoadedAt = Date.now();
            } catch (e) {
                console.error('[keepAliveView] 재조회 실패', e);
            }
        },
    },
};
